import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Lead, LeadStatus } from "@/components/lead-card";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  CalendarIcon,
  PhoneCallIcon,
  CheckCircleIcon,
  XCircleIcon,
  UsersIcon,
  BarChartIcon,
  ClockIcon,
  PhoneIcon,
  RefreshCw,
  FileWarning,
  CalendarCheck,
  PhoneForwarded,
  Clock,
} from "lucide-react";
import { AppSidebar } from "@/components/app-sidebar";
import { format, addDays, isToday, isAfter, isBefore, parseISO, startOfDay, endOfDay } from "date-fns";
import { de } from "date-fns/locale";
import { Progress } from "@/components/ui/progress";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { fetchLeadsForCurrentUser } from "@/services/leadsService";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useLocation } from "react-router-dom";
import { CustomerReferral } from "@/components/customer-referral";

export default function Dashboard() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [displayLeads, setDisplayLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalLeads: 0,
    activeLeads: 0,
    completedLeads: 0,
    upcomingCalls: 0,
  });

  // Load leads data on component mount
  useEffect(() => {
    const loadLeads = async () => {
      setIsLoading(true);
      try {
        // Use role-aware fetching - automatically filters by role and tenant
        const relevantLeads = await fetchLeadsForCurrentUser(
          currentUser.id,
          currentUser.role,
          currentUser.tenantId
        );

        setAllLeads(relevantLeads);
        setDisplayLeads(relevantLeads);
      } catch (error) {
        console.error("Error loading leads:", error);
        toast({
          title: "Fehler beim Laden der Leads",
          description: "Ihre Leads konnten nicht geladen werden. Bitte versuchen Sie es später erneut.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      loadLeads();
    }
  }, [currentUser, toast]);

  // Handle lead status change
  const handleLeadStatusChange = (leadId: string, newStatus: LeadStatus) => {
    setDisplayLeads(prevLeads =>
      prevLeads.map(lead =>
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      )
    );
  };

  // Helper function to safely format date with time
  const formatDateWithTime = (date: Date | string | undefined): string => {
    if (!date) return "";

    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return "";

      return format(dateObj, "dd. MMM HH:mm", { locale: de });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Summary stats
  const newLeadsCount = displayLeads.filter(lead => lead.status === "new").length;
  const notReachedLeadsCount = displayLeads.filter(lead => lead.status === "not-reached").length;
  const followUpLeadsCount = displayLeads.filter(lead => lead.status === "follow-up").length;
  const scheduledLeadsCount = displayLeads.filter(lead => lead.status === "scheduled").length;
  const inProgressLeadsCount = displayLeads.filter(lead => lead.status === "in-progress").length;
  const wonLeadsCount = displayLeads.filter(lead => lead.status === "won").length;
  const lostLeadsCount = displayLeads.filter(lead => lead.status === "lost").length;
  const wrongDataCount = displayLeads.filter(lead => lead.status === "wrong-data").length;
  const deleteDataCount = displayLeads.filter(lead => lead.status === "delete-data").length;

  // Upcoming callbacks (next 3 days)
  const today = new Date();
  const threeDaysFromNow = addDays(today, 3);

  const upcomingCallbacks = displayLeads
    .filter(lead => {
      if (!lead.nextContactDate) return false;

      const nextContactDate = lead.nextContactDate instanceof Date
        ? lead.nextContactDate
        : new Date(lead.nextContactDate);

      // Ensure the date is valid before proceeding with date-fns functions
      if (isNaN(nextContactDate.getTime())) return false;

      return (
        (isAfter(nextContactDate, startOfDay(today)) || isToday(nextContactDate)) && // Include today, from start of day
        isBefore(nextContactDate, endOfDay(threeDaysFromNow)) // Ensure it's before the end of the third day
      );
    })
    .sort((a, b) => {
      if (!a.nextContactDate || !b.nextContactDate) return 0;

      const dateA = a.nextContactDate instanceof Date ? a.nextContactDate : new Date(a.nextContactDate);
      const dateB = b.nextContactDate instanceof Date ? b.nextContactDate : new Date(b.nextContactDate);

      // Ensure dates are valid before getTime()
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;

      return dateA.getTime() - dateB.getTime();
    });

  const upcomingCallbacksCount = upcomingCallbacks.length;

  // Prepare data for line chart - convert status data to line chart format
  const lineChartData = [
    { name: "Neu", value: newLeadsCount, color: "#3b82f6" },
    { name: "Nicht erreicht", value: notReachedLeadsCount, color: "#f59e0b" },
    { name: "Wiedervorlage", value: followUpLeadsCount, color: "#8b5cf6" },
    { name: "Terminiert", value: scheduledLeadsCount, color: "#10b981" },
    { name: "In Prozess", value: inProgressLeadsCount, color: "#f97316" },
    { name: "Gewonnen", value: wonLeadsCount, color: "#22c55e" },
    { name: "Verloren", value: lostLeadsCount, color: "#ef4444" },
    { name: "Falsche Daten", value: wrongDataCount, color: "#94a3b8" },
    { name: "Daten löschen", value: deleteDataCount, color: "#64748b" }
  ].filter(item => item.value > 0);

  // Update the chart configuration
  const chartConfig = {
    new: { color: "#3b82f6" },
    "not-reached": { color: "#f59e0b" },
    "follow-up": { color: "#8b5cf6" },
    scheduled: { color: "#10b981" },
    "in-progress": { color: "#f97316" },
    won: { color: "#22c55e" },
    lost: { color: "#ef4444" },
    "wrong-data": { color: "#94a3b8" },
    "delete-data": { color: "#64748b" }
  };

  // Update the status badge display
  const getStatusBadge = (status: string) => {
    // Using labels from LeadCard.tsx and adapting colors for badge readability
    const statusConfig = {
      "new": { bg: "bg-purple-100", text: "text-purple-700", label: "Neu" },
      "old-contact": { bg: "bg-gray-100", text: "text-gray-700", label: "Altkontakt" },
      "not-reached": { bg: "bg-blue-100", text: "text-blue-700", label: "Nicht erreicht" },
      "follow-up": { bg: "bg-amber-100", text: "text-amber-700", label: "Wiedervorlage" },
      "scheduled": { bg: "bg-indigo-100", text: "text-indigo-700", label: "Terminiert" },
      "in-progress": { bg: "bg-orange-100", text: "text-orange-700", label: "In Prozess" },
      "won": { bg: "bg-green-100", text: "text-green-700", label: "Gewonnen" },
      "lost": { bg: "bg-red-100", text: "text-red-700", label: "Verloren" },
      "wrong-data": { bg: "bg-yellow-100", text: "text-yellow-700", label: "Falsche Daten" },
      "delete-data": { bg: "bg-pink-100", text: "text-pink-700", label: "Daten löschen" }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { bg: "bg-gray-100", text: "text-gray-700", label: status };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Calculate conversion rate
  const totalLeads = displayLeads.length;
  const conversionRate = totalLeads > 0 ? Math.round((wonLeadsCount / totalLeads) * 100) : 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-screen overflow-hidden bg-background">
        <AppSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Daten werden geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  // Admin view title modifier
  const dashboardTitle = `Willkommen, ${currentUser?.name || "Benutzer"}`;
  const dashboardSubtitle = "Ihr persönliches Lead-Management-Dashboard";

  // Check if we're on the customer referral page
  const isCustomerReferralPage = location.pathname === "/customer-referral";

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AppSidebar />
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="py-4 px-6 border-b">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <div>
              <h1 className="text-2xl font-bold">{dashboardTitle}</h1>
              <p className="text-muted-foreground">{dashboardSubtitle}</p>
            </div>
            <div className="flex items-center gap-2" />
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {isCustomerReferralPage ? (
            <CustomerReferral />
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                {/* Neu Leads */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Neue Leads</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <UsersIcon className="h-5 w-5 text-blue-500 mr-2" />
                      <div className="text-2xl font-bold">{newLeadsCount}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Wiedervorlage */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Wiedervorlage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <RefreshCw className="h-5 w-5 text-purple-500 mr-2" />
                      <div className="text-2xl font-bold">{followUpLeadsCount}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Gewonnen */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Gewonnen</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      <div className="text-2xl font-bold">{wonLeadsCount}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Falsche Daten */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Falsche Daten</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <FileWarning className="h-5 w-5 text-gray-500 mr-2" />
                      <div className="text-2xl font-bold">{wrongDataCount}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Nicht erreicht */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Nicht erreicht</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <BarChartIcon className="h-5 w-5 text-yellow-500 mr-2" />
                      <div className="text-2xl font-bold">{notReachedLeadsCount}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Terminiert */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Terminiert</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <CalendarCheck className="h-5 w-5 text-emerald-500 mr-2" />
                      <div className="text-2xl font-bold">{scheduledLeadsCount}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Verloren */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Verloren</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                      <div className="text-2xl font-bold">{lostLeadsCount}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* In Prozess */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">In Prozess</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-orange-500 mr-2" />
                      <div className="text-2xl font-bold">{inProgressLeadsCount}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Stat Cards and Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Lead Status Distribution - Now using Line Chart */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Lead Status Verteilung</CardTitle>
                    <CardDescription>
                      Übersicht über alle Leads nach aktuellem Status
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex justify-center items-center h-80">
                    <ChartContainer
                      className="w-full h-full"
                      config={chartConfig}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={lineChartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis
                            dataKey="name"
                            padding={{ left: 20, right: 20 }}
                          />
                          <YAxis />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                return (
                                  <div className="bg-background border rounded-md px-3 py-2 shadow-md">
                                    <p className="font-medium">{`${payload[0].payload.name}`}</p>
                                    <p className="text-sm">{`Anzahl: ${payload[0].value}`}</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#8884d8"
                            strokeWidth={2}
                            dot={{ strokeWidth: 2, r: 6 }}
                            activeDot={{ r: 8 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </CardContent>
                </Card>

                {/* Conversion Rate */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Konversionsrate</CardTitle>
                    <CardDescription>
                      Prozentsatz gewonnener Leads
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col items-center gap-4">
                    <div className="w-32 h-32 rounded-full bg-muted flex items-center justify-center border-8 border-primary/30">
                      <span className="text-3xl font-bold">{conversionRate}%</span>
                    </div>
                    <div className="w-full space-y-1">
                      <div className="flex justify-between text-xs">
                        <span>Leads gesamt</span>
                        <span>{totalLeads}</span>
                      </div>
                      <Progress value={conversionRate} className="h-2" />
                      <div className="flex justify-between text-xs">
                        <span>Gewonnen</span>
                        <span>{wonLeadsCount}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Upcoming Callbacks & Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Upcoming Callbacks - Now visible for ALL users */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Anstehende Rückrufe</CardTitle>
                    <CardDescription>
                      Die nächsten Termine in den kommenden 3 Tagen
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {upcomingCallbacks.length > 0 ? (
                      <div className="space-y-4">
                        {upcomingCallbacks.slice(0, 5).map(callback => (
                          <div key={callback.id} className="flex items-start space-x-3 pb-3 border-b last:border-b-0">
                            <div className="bg-primary/10 rounded-full p-2">
                              <PhoneCallIcon className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{`${callback.firstName} ${callback.lastName}`}</p>
                                <p className="text-xs text-muted-foreground">
                                  {callback.nextContactDate && formatDateWithTime(callback.nextContactDate)}
                                </p>
                              </div>

                              {callback.attachment && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <span className="inline-block bg-muted px-1.5 py-0.5 rounded-sm">
                                    {callback.attachment}
                                  </span>
                                </p>
                              )}
                              <div className="flex items-center gap-1 text-xs">
                                <CalendarIcon className="h-3 w-3" />
                                <span>
                                  {callback.nextContactDate && format(new Date(callback.nextContactDate), "HH:mm 'Uhr'", { locale: de })}
                                </span>
                              </div>
                            </div>
                            <Button variant="outline" size="icon" className="h-8 w-8">
                              <PhoneIcon className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-40 flex items-center justify-center text-muted-foreground">
                        <p>Keine anstehenden Rückrufe</p>
                      </div>
                    )}

                    {upcomingCallbacks.length > 0 && (
                      <div className="mt-4">
                        <Button variant="outline" className="w-full" size="sm">
                          Alle Termine anzeigen
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Activity - Always visible */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Aktuelle Aktivitäten</CardTitle>
                    <CardDescription>
                      Die letzten Änderungen im Lead-Status
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {displayLeads.length > 0 ? (
                      <div className="space-y-4">
                        {displayLeads.slice(0, 5).map((lead) => (
                          <div key={lead.id} className="flex items-start space-x-3 pb-3 border-b last:border-b-0">
                            <div className={`rounded-full p-2 ${lead.status === "won" ? "bg-green-100" :
                              lead.status === "lost" ? "bg-red-100" :
                                lead.status === "in-progress" ? "bg-orange-100" :
                                  "bg-gray-100"
                              }`}>
                              <ClockIcon className={`h-4 w-4 ${lead.status === "won" ? "text-green-500" :
                                lead.status === "lost" ? "text-red-500" :
                                  lead.status === "in-progress" ? "text-orange-500" :
                                    "text-gray-500"
                                }`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{`${lead.firstName} ${lead.lastName}`}</p>
                                {getStatusBadge(lead.status)}
                              </div>
                              <p className="text-sm text-muted-foreground">{lead.position || "Keine Position angegeben"}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-40 flex items-center justify-center text-muted-foreground">
                        <p>Keine Aktivitäten vorhanden</p>
                      </div>
                    )}

                    <div className="mt-4">
                      <Button
                        variant="outline"
                        className="w-full"
                        size="sm"
                        onClick={() => window.location.href = "/leads"}
                      >
                        Alle Leads anzeigen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
